import React from 'react';

interface PrivacyPolicyPageProps {
  onNavigate?: (path: string, view: string, tab?: string) => void;
}

export const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onNavigate }) => {
  const handleNav = (path: string, view: string, tab: string = 'water') => {
    if (onNavigate) {
      onNavigate(path, view, tab);
    } else {
      window.location.href = path;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <article className="bg-white dark:bg-[#1F2937] rounded-3xl p-6 sm:p-10 lg:p-12 space-y-8 shadow-xs border border-slate-200 dark:border-slate-700">
        <header className="border-b border-slate-100 dark:border-slate-700/80 pb-6">
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-900/40 text-[#087F9C] dark:text-[#39BFD3] border border-teal-200/60 dark:border-teal-800/60 inline-block mb-3">
            Legal &amp; Data Transparency
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
            Privacy Policy
          </h1>
          <div className="text-xs text-slate-400 mt-2 flex flex-wrap gap-4">
            <span>Effective Date: October 31, 2026</span>
            <span>Last Updated: November 2026</span>
            <span>Applicable to: PipeSizingCalculator.com</span>
          </div>
        </header>

        {/* 1. Introduction */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            1. Introduction
          </h2>
          <p>
            At PipeSizingCalculator.com (&quot;we&quot;, &quot;our&quot;, or &quot;the Website&quot;), we respect your privacy and are committed to maintaining transparent practices regarding information collected when you access and use our web-based engineering calculation suite.
          </p>
          <p>
            This Privacy Policy details what information is collected, how local client-side storage is utilized, the role of third-party advertising and content delivery networks, and how you can exercise control over your personal data.
          </p>
        </section>

        {/* 2. Client-Side Calculations & Zero Account Policy */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            2. Local Execution &amp; No Account Registration
          </h2>
          <p>
            Our calculation suite is architected for privacy and performance. You do not need to register, create a user account, or provide personal credentials to use any of our calculators:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>
              <strong>Client-Side Processing:</strong> All hydraulic formulas (flow rates, velocities, Reynolds numbers, Darcy friction factors, head losses, and pipe schedule dimensions) run locally in your web browser&apos;s JavaScript engine. Your calculation inputs and project flow parameters are never uploaded to, processed by, or stored on our web servers.
            </li>
            <li>
              <strong>PDF Engineering Reports:</strong> PDF report generation operates via your browser&apos;s native print rendering pipeline. Report generation takes place completely in device memory without transmitting document data externally.
            </li>
          </ul>
        </section>

        {/* 3. Browser Storage & Local Preferences */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            3. Local Storage &amp; Cookies
          </h2>
          <p>
            We use your browser&apos;s modern Web Storage API (specifically <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-xs font-mono text-slate-800 dark:text-slate-200">localStorage</code>) strictly to preserve user preferences between page views and visits:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>
              <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-xs font-mono text-slate-800 dark:text-slate-200">pipe-theme</code>: Stores your selected color theme preference (<code className="px-1 py-0.5 text-xs font-mono">dark</code> or <code className="px-1 py-0.5 text-xs font-mono">light</code>).
            </li>
            <li>
              <code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-xs font-mono text-slate-800 dark:text-slate-200">pipe-lang</code>: Remembers your active language interface preference.
            </li>
          </ul>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            These values reside exclusively on your local device and can be cleared at any time by clearing your browser cache and site storage.
          </p>
        </section>

        {/* 4. Third-Party Advertising & Google AdSense */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            4. Third-Party Advertising (Google AdSense)
          </h2>
          <p>
            To keep our engineering calculation suite free and accessible worldwide, we partner with third-party advertising vendors, including Google AdSense, to display advertisements when you visit our website.
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>
              <strong>Advertising Cookies:</strong> Google and its advertising partners use cookies (such as the Google DoubleClick cookie) to serve ads to users based on prior visits to this website and other websites across the Internet.
            </li>
            <li>
              <strong>Personalized Advertising:</strong> These cookies enable Google and its certified ad partners to serve advertisements tailored to your general browsing interests.
            </li>
            <li>
              <strong>Opt-Out Choices:</strong> You may opt out of personalized advertising at any time by visiting{' '}
              <a
                href="https://www.google.com/settings/ads"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#087F9C] dark:text-[#39BFD3] underline font-medium hover:opacity-80"
              >
                Google Ads Settings
              </a>. Alternatively, you can opt out of third-party vendor cookies for personalized advertising by visiting{' '}
              <a
                href="https://www.aboutads.info/choices/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#087F9C] dark:text-[#39BFD3] underline font-medium hover:opacity-80"
              >
                AboutAds.info
              </a>.
            </li>
          </ul>
        </section>

        {/* 5. Server Logs & Web Infrastructure */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            5. Server Logs &amp; Content Delivery Networks (CDNs)
          </h2>
          <p>
            When you request pages on our site, our web hosting servers and content delivery networks automatically record standard internet transmission metadata. This information typically includes:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm text-slate-700 dark:text-slate-300">
            <li>Your Internet Protocol (IP) address</li>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Referring URL and requested page paths</li>
            <li>Date and time stamps of requests</li>
          </ul>
          <p>
            This server log data is used strictly for system security, malicious traffic mitigation (such as DDoS prevention), diagnosing technical server errors, and ensuring overall platform stability.
          </p>
          <p>
            Additionally, the Website loads typography assets from Google Fonts (<code className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-xs font-mono text-slate-800 dark:text-slate-200">fonts.googleapis.com</code>) and stylesheet libraries from content delivery networks, which may log IP requests in accordance with their respective privacy policies.
          </p>
        </section>

        {/* 6. User Feedback & Communication */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            6. Information Submitted Through Feedback
          </h2>
          <p>
            If you voluntarily submit feedback, bug reports, or formula verification inquiries through our{' '}
            <button
              onClick={() => handleNav('/contact-us/', 'contact')}
              className="text-[#087F9C] dark:text-[#39BFD3] underline font-semibold hover:opacity-80"
            >
              Contact Us page
            </button>, we receive the contact details and message contents you provide. This information is used exclusively to evaluate the technical report, reply to your inquiry, or correct calculation code. We never sell, rent, or distribute user correspondence.
          </p>
        </section>

        {/* 7. Children's Privacy */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            7. Children&apos;s Privacy
          </h2>
          <p>
            PipeSizingCalculator.com is an engineering and educational resource not directed to children under 13 years of age. We do not knowingly collect personal identifiable information from children under 13.
          </p>
        </section>

        {/* 8. Changes to This Privacy Policy */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            8. Policy Updates &amp; Contact Information
          </h2>
          <p>
            We may revise this Privacy Policy periodically to reflect changes in legal requirements, third-party advertising partner policies, or website functionality. The effective date at the top of this page indicates the most recent update.
          </p>
          <p>
            If you have questions regarding this Privacy Policy or wish to inquire about our data handling practices, please submit an inquiry via our{' '}
            <button
              onClick={() => handleNav('/contact-us/', 'contact')}
              className="text-[#087F9C] dark:text-[#39BFD3] underline font-semibold hover:opacity-80"
            >
              Contact &amp; Feedback Form
            </button>.
          </p>
        </section>

        {/* Navigation Quick Links */}
        <footer className="pt-6 border-t border-slate-100 dark:border-slate-700/80 flex flex-wrap gap-3 text-xs font-semibold">
          <button
            onClick={() => handleNav('/', 'calculators', 'water')}
            className="px-4 py-2.5 bg-[#087F9C] text-white rounded-xl hover:bg-teal-700 transition-colors"
          >
            Launch Water Pipe Sizing &rarr;
          </button>
          <button
            onClick={() => handleNav('/terms-and-conditions/', 'terms')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Terms and Conditions &rarr;
          </button>
          <button
            onClick={() => handleNav('/disclaimer/', 'disclaimer')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Engineering Disclaimer &rarr;
          </button>
        </footer>
      </article>
    </div>
  );
};
